import { useState, useEffect, useCallback } from "react";
import CoachSummaryTable from "./components/CoachSummaryTable";
import CoachSummaryCharts from "./components/CoachSummaryCharts";
import CoachWeekDrawer, { type CoachWeekSelection } from "./components/CoachWeekDrawer";
import type { CoachSummaryRecord } from "@/mocks/coachSummary";
import { fetchAttendanceWeeks } from "@/services/attendance";
import AppShell from "@/components/AppShell";

const WEEKS_PER_PAGE = 10;

export default function CoachSummaryPage() {
  const [records, setRecords] = useState<CoachSummaryRecord[]>([]);
  const [selection, setSelection] = useState<CoachWeekSelection | null>(null);
  const [loading, setLoading] = useState(true);
  const [paging, setPaging] = useState(false);
  const [oldestWeekKey, setOldestWeekKey] = useState<string | null>(null);
  const [newestWeekKey, setNewestWeekKey] = useState<string | null>(null);
  const [hasOlder, setHasOlder] = useState(false);
  const [hasNewer, setHasNewer] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showHighRisk, setShowHighRisk] = useState(false);
  const [refreshToken, setRefreshToken] = useState(0);

  const applyResult = useCallback((result: Awaited<ReturnType<typeof fetchAttendanceWeeks>>) => {
    setRecords(result.records);
    setOldestWeekKey(result.oldestWeekKey);
    setNewestWeekKey(result.newestWeekKey);
    setHasOlder(result.hasOlder);
    setHasNewer(result.hasNewer);
  }, []);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        applyResult(await fetchAttendanceWeeks(WEEKS_PER_PAGE));
      } catch (err) {
        console.error('Failed to load attendance data:', err);
        setError('Failed to load attendance data from API.');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [applyResult, refreshToken]);

  const goPrev = useCallback(async () => {
    if (!oldestWeekKey || paging) return;
    try {
      setPaging(true);
      applyResult(await fetchAttendanceWeeks(WEEKS_PER_PAGE, { before: oldestWeekKey }));
    } catch (err) {
      console.error('Failed to load older weeks:', err);
      setError('Failed to load older weeks from API.');
    } finally {
      setPaging(false);
    }
  }, [oldestWeekKey, paging, applyResult]);

  const goNext = useCallback(async () => {
    if (!newestWeekKey || paging) return;
    try {
      setPaging(true);
      applyResult(await fetchAttendanceWeeks(WEEKS_PER_PAGE, { after: newestWeekKey }));
    } catch (err) {
      console.error('Failed to load newer weeks:', err);
      setError('Failed to load newer weeks from API.');
    } finally {
      setPaging(false);
    }
  }, [newestWeekKey, paging, applyResult]);

  const overall = records.find(r => r.coachName === "OVERALL COMPANY");
  const coaches = records.filter(r => r.coachName !== "OVERALL COMPANY");
  const highRiskCoaches = coaches
    .filter(c => c.last10WeeksAbsenceRatio >= 20)
    .sort((a, b) => b.last10WeeksAbsenceRatio - a.last10WeeksAbsenceRatio);
  const highRisk = highRiskCoaches.length;
  const totalStudents = overall?.studentsCount ?? coaches.reduce((s, c) => s + c.studentsCount, 0);
  // Company-wide pooled ratio over the window (the Overall Company row), not a
  // mean of coach ratios — coaches with no attendance records would drag that to 0.
  const avgAbsence = (overall?.last10WeeksAbsenceRatio ?? 0).toFixed(1);

  // Range of the weeks currently on screen (weeks are newest first).
  const shownWeeks = (overall ?? coaches[0])?.weeks ?? [];
  const newestWeek = shownWeeks[0];
  const oldestWeek = shownWeeks[shownWeeks.length - 1];
  const fmtDay = (iso?: string) =>
    iso ? new Date(`${iso}T00:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "";
  const weeksRange = newestWeek && oldestWeek
    ? `${oldestWeek.label} – ${newestWeek.label} · ${fmtDay(oldestWeek.weekStart)} – ${fmtDay(newestWeek.weekEnd)}`
    : "";

  const handleWeekSelect = (coachName: string, weekIndex: number) => {
    const record = records.find((r) => r.coachName === coachName);
    const week = record?.weeks[weekIndex];
    setSelection({
      coachName,
      year: week?.year,
      weekNumber: week?.weekNumber,
      label: week?.label,
      weekStart: week?.weekStart,
      weekEnd: week?.weekEnd,
    });
  };

  return (
    <>
      {loading && (
        <div style={{
          position: "fixed", inset: 0,
          background: "rgba(247,248,250,0.85)",
          display: "flex", alignItems: "center", justifyContent: "center",
          zIndex: 50,
        }}>
          <div style={{
            background: "var(--color-surface)", border: "1px solid var(--color-border)",
            borderRadius: "var(--radius-lg)", padding: "var(--space-8)",
            display: "flex", flexDirection: "column", alignItems: "center", gap: "var(--space-4)",
            boxShadow: "var(--shadow-raised)",
          }}>
            <i className="ri-loader-4-line" style={{ fontSize: 36, color: "var(--color-accent)", animation: "spin 1s linear infinite" }} aria-hidden="true" />
            <span style={{ color: "var(--color-text-secondary)", fontSize: "var(--text-sm)" }}>Loading coach summary…</span>
          </div>
        </div>
      )}

      {error && (
        <div role="alert" style={{
          position: "fixed", top: "var(--space-4)", right: "var(--space-4)",
          background: "var(--color-danger-bg)", border: "1px solid var(--color-danger)",
          color: "var(--color-danger)", padding: "var(--space-3) var(--space-4)",
          borderRadius: "var(--radius-md)", boxShadow: "var(--shadow-raised)",
          zIndex: 50, display: "flex", alignItems: "center", gap: "var(--space-2)",
        }}>
          <i className="ri-error-warning-line" style={{ fontSize: 18 }} aria-hidden="true" />
          <span style={{ fontSize: "var(--text-sm)" }}>{error}</span>
        </div>
      )}

      <AppShell
        topbarLeft={
          <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)" }}>
            <h1 style={{ margin: 0, fontSize: "var(--text-md)", fontWeight: "var(--font-semibold)" }}>
              Coach Summary
            </h1>
            <button
              type="button"
              onClick={() => setRefreshToken((value) => value + 1)}
              disabled={loading}
              style={{
                border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)",
                background: "var(--color-surface)", color: "var(--color-text-secondary)",
                padding: "4px 10px", cursor: loading ? "wait" : "pointer", fontSize: "var(--text-xs)",
              }}
              title="Reload live attendance data"
            >
              ↻ Refresh data
            </button>
          </div>
        }
      >
        <div className="page-layout">
          {/* Page heading */}
          <div className="page-heading-row">
            <div>
              <h2 style={{ margin: 0 }}>Absence Ratio Overview</h2>
              {weeksRange && (
                <p style={{
                  margin: "var(--space-2) 0 0", display: "flex", alignItems: "center", flexWrap: "wrap",
                  gap: "var(--space-2)", fontSize: "var(--text-sm)", color: "var(--color-text-secondary)",
                }}>
                  <span style={{
                    display: "inline-flex", alignItems: "center", gap: 6,
                    background: "var(--color-accent-tint)", color: "var(--color-accent)",
                    padding: "2px 10px", borderRadius: 999, fontWeight: "var(--font-semibold)",
                  }}>
                    <i className="ri-calendar-line" aria-hidden="true" />
                    {hasNewer ? `${shownWeeks.length} weeks` : `Last ${shownWeeks.length} weeks`}
                  </span>
                  <span className="tabular-nums">{weeksRange}</span>
                  <span style={{ color: "var(--color-text-muted)" }}>· all figures on this page cover these weeks</span>
                </p>
              )}
            </div>
          </div>

          {/* KPI hero row */}
          <div className="kpi-grid">
            {[
              { label: "Total Coaches", value: coaches.length, icon: "ri-shield-user-line", status: null, onClick: null },
              { label: "Total Students", value: totalStudents, icon: "ri-group-line", status: null, onClick: null },
              { label: "Avg 10W Absence", value: `${avgAbsence}%`, icon: "ri-percent-line",
                status: parseFloat(avgAbsence) >= 20 ? "kpi-status--danger" : parseFloat(avgAbsence) >= 15 ? "kpi-status--warning" : "kpi-status--success", onClick: null },
              { label: "Company Last Week Absence", value: `${(overall?.weeks[0]?.absenceRatio || 0).toFixed(2)}%`, icon: "ri-building-line", status: null, onClick: null },
              { label: "High Risk Coaches (≥20%)", value: highRisk, icon: "ri-alarm-warning-line",
                status: highRisk > 3 ? "kpi-status--danger" : highRisk > 1 ? "kpi-status--warning" : "kpi-status--success",
                onClick: highRisk > 0 ? () => setShowHighRisk(true) : null },
            ].map((s) => {
              const statusLabel = s.status === "kpi-status--danger" ? "⚠ High Risk"
                : s.status === "kpi-status--warning" ? "△ Need Attention"
                : s.status === "kpi-status--success" ? "✓ On Track" : null;
              const clickable = Boolean(s.onClick);
              return (
                <div
                  key={s.label}
                  className={`kpi-card${clickable ? " kpi-card-clickable" : ""}`}
                  onClick={s.onClick ?? undefined}
                  onKeyDown={clickable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); s.onClick?.(); } } : undefined}
                  role={clickable ? "button" : undefined}
                  tabIndex={clickable ? 0 : undefined}
                  title={clickable ? "View high-risk coaches" : undefined}
                  style={clickable ? { cursor: "pointer" } : undefined}
                >
                  <div className="kpi-header">
                    <span className="kpi-label">{s.label}</span>
                    <i className={`${s.icon} kpi-info`} aria-hidden="true" />
                  </div>
                  <div className="kpi-body">
                    <span className="kpi-value stat-value">{s.value}</span>
                  </div>
                  {statusLabel && (
                    <div className="kpi-footer">
                      <span className={`kpi-status ${s.status}`}>{statusLabel}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Table */}
          <CoachSummaryTable
            records={records}
            onWeekSelect={handleWeekSelect}
            onPrev={goPrev}
            onNext={goNext}
            canPrev={hasOlder}
            canNext={hasNewer}
            paging={paging}
          />

          {/* Charts */}
          <CoachSummaryCharts records={records} />
        </div>
      </AppShell>

      <CoachWeekDrawer selection={selection} onClose={() => setSelection(null)} />

      {showHighRisk && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="High risk coaches"
          onClick={() => setShowHighRisk(false)}
          style={{
            position: "fixed", inset: 0, background: "rgba(15,22,41,0.45)",
            display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60,
            padding: "var(--space-4)",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "var(--color-surface)", border: "1px solid var(--color-border)",
              borderRadius: "var(--radius-lg)", boxShadow: "var(--shadow-raised)",
              width: "min(440px, 100%)", maxHeight: "80vh", display: "flex", flexDirection: "column",
            }}
          >
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "var(--space-4)", borderBottom: "1px solid var(--color-border)",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
                <i className="ri-alarm-warning-line" style={{ color: "var(--color-danger)", fontSize: 20 }} aria-hidden="true" />
                <h3 style={{ margin: 0, fontSize: "var(--text-md)", fontWeight: "var(--font-semibold)" }}>
                  High Risk Coaches (≥20%)
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setShowHighRisk(false)}
                aria-label="Close"
                style={{
                  border: "none", background: "transparent", cursor: "pointer",
                  color: "var(--color-text-secondary)", fontSize: 20, lineHeight: 1, padding: 4,
                }}
              >
                <i className="ri-close-line" aria-hidden="true" />
              </button>
            </div>

            <div className="themed-scrollbar" style={{ overflowY: "auto", padding: "var(--space-2) 0" }}>
              {highRiskCoaches.length === 0 ? (
                <p style={{ padding: "var(--space-4)", color: "var(--color-text-secondary)", fontSize: "var(--text-sm)" }}>
                  No coaches are currently at high risk.
                </p>
              ) : (
                highRiskCoaches.map((c) => (
                  <div
                    key={c.coachName}
                    style={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      gap: "var(--space-3)", padding: "var(--space-2) var(--space-4)",
                    }}
                  >
                    <span style={{ fontSize: "var(--text-sm)", color: "var(--color-text-primary)" }}>{c.coachName}</span>
                    <span className="kpi-status kpi-status--danger" style={{ whiteSpace: "nowrap" }}>
                      {c.last10WeeksAbsenceRatio.toFixed(1)}%
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .kpi-card-clickable { transition: transform 0.12s ease, box-shadow 0.12s ease, border-color 0.12s ease; }
        .kpi-card-clickable:hover { transform: translateY(-2px); box-shadow: var(--shadow-raised); border-color: var(--color-accent); }
        .kpi-card-clickable:focus-visible { outline: 2px solid var(--color-accent); outline-offset: 2px; }
      `}</style>
    </>
  );
}
